import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActiveStatuses, fetchScans } from '../store/scanSlice.js';
import type { AppDispatch, RootState } from '../store/index.js';
import { TERMINAL_SCAN_LABEL_KEYS } from '../../shared/types.js';

const POLL_INTERVAL_MS = 3000;

/**
 * Smart Polling fuer /scans/active-statuses.
 *
 * - Start: sofort beim Mount.
 * - Stop: wenn keine aktiven (nicht-terminalen) Scans mehr existieren.
 * - Restart: wenn `enabled` wechselt oder sich neue Scans ergeben (z.B. nach einem startScan).
 * - Refetch: wenn aktive Scans verschwinden (= terminal geworden), wird die Archiv-Liste neu geladen.
 *
 * Netzwerkfehler werden absichtlich geschluckt (PRD §6 — Silent Retry).
 */
export function useScanStatusPolling(enabled: boolean = true): void {
  const dispatch = useDispatch<AppDispatch>();
  const activeStatuses = useSelector((state: RootState) => state.scan.activeStatuses);
  const pagination = useSelector((state: RootState) => state.scan.pagination);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);
  const prevActiveIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    const scheduleNext = () => {
      if (!mountedRef.current) return;
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    const poll = async () => {
      try {
        const result = await dispatch(fetchActiveStatuses()).unwrap();
        const terminal = new Set<string>(TERMINAL_SCAN_LABEL_KEYS);
        const currentActiveIds = new Set(
          result
            .filter((s) => !s.scan_label || !terminal.has(s.scan_label.key_name))
            .map((s) => s.id),
        );

        const prevIds = prevActiveIdsRef.current;
        const scansCompleted = prevIds.size > 0 && [...prevIds].some((id) => !currentActiveIds.has(id));

        if (scansCompleted) {
          dispatch(fetchScans({ page: pagination.page, limit: pagination.limit }));
        }

        prevActiveIdsRef.current = currentActiveIds;

        if (currentActiveIds.size > 0) {
          scheduleNext();
        } else if (prevIds.size > 0) {
          dispatch(fetchScans({ page: pagination.page, limit: pagination.limit }));
        }
      } catch {
        scheduleNext();
      }
    };

    void poll();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, enabled]);

  void activeStatuses;
}
